// pages/vehicles/VehicleFormPage.tsx
import {
    Page,
    Card,
    Form,
    FormLayout,
    TextField,
    Button,
    InlineError,
    Text,
    Banner,
} from '@shopify/polaris';
import React, { useCallback, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../app/stores/store';
// import {useNavigate} from 'react-router-dom';

type FieldErrors = {
    yearFrom?: string;
    yearTo?: string;
    make?: string;
    model?: string;
};

const YEAR_MIN = 1886;
const YEAR_MAX = 2100;

const VehicleFormPage: React.FC = () => {
    const { vehicleStore } = useStore();
    // const navigate = useNavigate();

    // ---- Suggestions (replace with data from store/API as needed) ----
    const makeSuggestions = useMemo(
        () => ['Toyota', 'Honda', 'Ford', 'Chevrolet', 'BMW', 'Mercedes-Benz', 'Volkswagen', 'Nissan', 'Hyundai', 'Kia'],
        [],
    );
    const modelSuggestions = useMemo(
        () => ['Camry', 'Civic', 'F-150', 'Silverado', '3 Series', 'C-Class', 'Golf', 'Altima', 'Elantra', 'Sorento'],
        [],
    );
    const submodelSuggestions = useMemo(
        () => ['Base', 'SE', 'LE', 'XLE', 'Sport', 'Limited', 'Touring', 'Premium'],
        [],
    );

    // ---- Form state ----
    const [yearFrom, setYearFrom] = useState('');
    const [yearTo, setYearTo] = useState('');
    const [make, setMake] = useState('');
    const [model, setModel] = useState('');
    const [submodel, setSubmodel] = useState('');

    // ---- Inline suggestion state ----
    const [makeSuggestion, setMakeSuggestion] = useState<string | undefined>();
    const [modelSuggestion, setModelSuggestion] = useState<string | undefined>();
    const [submodelSuggestion, setSubmodelSuggestion] = useState<string | undefined>();

    // ---- Errors ----
    const [errors, setErrors] = useState<FieldErrors>({});
    const [submitError, setSubmitError] = useState<string | undefined>();
    const [submitting, setSubmitting] = useState(false);

    // ---- Helpers ----
    const findSuggestion = (val: string, list: string[]) =>
        val ? list.find(s => s.toLowerCase().startsWith(val.toLowerCase())) : undefined;

    const clampDigits = (val: string) => val.replace(/[^\d]/g, '').slice(0, 4);

    const validate = (): boolean => {
        const e: FieldErrors = {};

        // Make & Model required
        if (!make.trim()) e.make = 'Make is required';
        if (!model.trim()) e.model = 'Model is required';

        // Years optional, but must be valid if present
        const yf = yearFrom ? parseInt(yearFrom, 10) : undefined;
        const yt = yearTo ? parseInt(yearTo, 10) : undefined;

        const inRange = (y: number) => y >= YEAR_MIN && y <= YEAR_MAX;

        if (yearFrom && (isNaN(yf!) || !inRange(yf!))) {
            e.yearFrom = `Enter a valid year between ${YEAR_MIN}-${YEAR_MAX}`;
        }
        if (yearTo && (isNaN(yt!) || !inRange(yt!))) {
            e.yearTo = `Enter a valid year between ${YEAR_MIN}-${YEAR_MAX}`;
        }
        if (!e.yearFrom && !e.yearTo && yf !== undefined && yt !== undefined && yf > yt) {
            e.yearTo = 'End year must be greater than or equal to start year';
        }

        setErrors(e);
        return Object.keys(e).length === 0;
    };

    // ---- Change handlers with suggestions ----
    const onYearFromChange = useCallback((v: string) => setYearFrom(clampDigits(v)), []);
    const onYearToChange = useCallback((v: string) => setYearTo(clampDigits(v)), []);

    const onMakeChange = useCallback(
        (v: string) => {
            setMake(v);
            setMakeSuggestion(findSuggestion(v, makeSuggestions));
        },
        [makeSuggestions],
    );
    const onModelChange = useCallback(
        (v: string) => {
            setModel(v);
            setModelSuggestion(findSuggestion(v, modelSuggestions));
        },
        [modelSuggestions],
    );
    const onSubmodelChange = useCallback(
        (v: string) => {
            setSubmodel(v);
            setSubmodelSuggestion(findSuggestion(v, submodelSuggestions));
        },
        [submodelSuggestions],
    );

    const acceptSuggestion = (current: string, suggestion?: string) =>
        suggestion && suggestion.toLowerCase().startsWith(current.toLowerCase()) ? suggestion : current;

    const onKeyDownApplySuggestion = useCallback(
        (setter: (v: string) => void, current: string, suggestion?: string) =>
            (e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === 'Tab') {
                    setter(acceptSuggestion(current, suggestion));
                }
            },
        [],
    );

    // ---- Submit ----
    const onSave = useCallback(async () => {
        setSubmitError(undefined);
        if (!validate()) return;

        setSubmitting(true);
        try {
            const payload = {
                yearFrom: yearFrom ? parseInt(yearFrom, 10) : null,
                yearTo: yearTo ? parseInt(yearTo, 10) : null,
                make: make.trim(),
                model: model.trim(),
                submodel: submodel.trim() || null,
            };

            // TODO: wire to your store
            // await vehicleStore.create(payload);
            // navigate('/apps/partfinder-development/vehicles'); // or your route
        } catch (err: any) {
            setSubmitError(err?.message ?? 'Failed to create vehicle');
        } finally {
            setSubmitting(false);
        }
    }, [yearFrom, yearTo, make, model, submodel /*, vehicleStore */]);

    const onCancel = useCallback(() => {
        // navigate back
    }, []);

    return (
        <Page
            title="Add vehicle"
            backAction={{ content: 'Vehicles', onAction: onCancel }}
            primaryAction={{ content: 'Save', onAction: onSave, loading: submitting }}
            secondaryActions={[{ content: 'Cancel', onAction: onCancel, disabled: submitting }]}
        >
            <Card>
                <Form onSubmit={e => { e.preventDefault(); onSave(); }}>
                    <FormLayout>
                        {submitError && (
                            <Banner tone="critical">
                                <Text as="p">{submitError}</Text>
                            </Banner>
                        )}

                        <Text variant="headingMd" as="h2">
                            Vehicle details
                        </Text>

                        <FormLayout.Group condensed>
                            <TextField
                                label="Year (from)"
                                value={yearFrom}
                                onChange={onYearFromChange}
                                placeholder="e.g. 2010"
                                inputMode="numeric"
                                error={errors.yearFrom}
                                autoComplete="off"
                            />
                            <TextField
                                label="Year (to)"
                                value={yearTo}
                                onChange={onYearToChange}
                                placeholder="e.g. 2018"
                                inputMode="numeric"
                                error={errors.yearTo}
                                autoComplete="off"
                            />
                        </FormLayout.Group>

                        <div onKeyDown={onKeyDownApplySuggestion(setMake, make, makeSuggestion)}>
                            <TextField
                                label="Make"
                                value={make}
                                onChange={onMakeChange}
                                error={errors.make}
                                autoComplete="off"
                                suggestion={makeSuggestion}
                                placeholder="e.g. Toyota"
                            />
                        </div>

                        <div onKeyDown={onKeyDownApplySuggestion(setModel, model, modelSuggestion)}>
                            <TextField
                                label="Model"
                                value={model}
                                onChange={onModelChange}
                                error={errors.model}
                                autoComplete="off"
                                suggestion={modelSuggestion}
                                placeholder="e.g. Camry"
                            />
                        </div>

                        <div onKeyDown={onKeyDownApplySuggestion(setSubmodel, submodel, submodelSuggestion)}>
                            <TextField
                                label="Submodel (optional)"
                                value={submodel}
                                onChange={onSubmodelChange}
                                autoComplete="off"
                                suggestion={submodelSuggestion}
                                placeholder="e.g. SE"
                            />
                        </div>

                        {/* Optional combined error example if you want one spot */}
                        {errors.make && errors.model && (
                            <InlineError message="Make and Model are required" fieldID="make" />
                        )}

                        <FormLayout.Group>
                            <Button onClick={onCancel} disabled={submitting}>
                                Cancel
                            </Button>
                            <Button variant="primary" onClick={onSave} loading={submitting}>
                                Save
                            </Button>
                        </FormLayout.Group>
                    </FormLayout>
                </Form>
            </Card>
        </Page>
    );
};

export default observer(VehicleFormPage);
